import { BillingBudgetCard } from '@/components/features/organisations/BillingBudgetCard'
import { BillingCreditPlanPicker } from '@/components/features/organisations/BillingCreditPlanPicker'
import { BillingCreditsCard } from '@/components/features/organisations/BillingCreditsCard'
import { BillingInvoicesCard } from '@/components/features/organisations/BillingInvoicesCard'
import {
  BillingPaymentMethodCard,
  CheckoutReturnBanner,
  PaymentReturnBanner,
  PaymentReturnStatus,
} from '@/components/features/organisations/BillingPaymentMethodCard'
import {
  BillingPlanCard,
  billingActionFailure,
} from '@/components/features/organisations/BillingPlanCard'
import { BillingStorageCard } from '@/components/features/organisations/BillingStorageCard'
import { useUpgradeSettlementPoll } from '@/components/features/organisations/OrgFrozenScreen'
import { useClient } from '@/context/ClientContext'
import { useBillingCheckout } from '@/hooks/useBillingCheckout'
import { showsPaymentMethodCard } from '@/lib/billing-payment-card'
import {
  isCheckoutReturnConfirmed,
  rememberCheckoutReturnOrg,
  takeCheckoutHadPaymentMethod,
} from '@/lib/checkout-return'
import {
  forgetCreditPlanDecline,
  forgetCreditPlanIntent,
  readCreditPlanDecline,
  readCreditPlanIntent,
  rememberCreditPlanDecline,
  rememberCreditPlanIntent,
} from '@/lib/credit-plan-intent'
import {
  PAYMENT_CONFIRM_POLL_MS,
  PAYMENT_CONFIRM_TIMEOUT_MS,
  isPaymentConfirmed,
  rememberPaymentReturn,
  takePaymentReturn,
} from '@/lib/payment-return'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useLoaderData,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import {
  BILLING_PLAN_CATALOGUE,
  CREDIT_PLAN_CATALOGUE,
  SelfServeCreditPlanTier,
  hasPerOrgBundle,
  isAwaitingFirstPlan,
  requiresSelfServePaymentMethod,
  starterTrialProgress,
} from '@droidmap/shared-service-contract'

/** Keeps the org re-reading while a Trial's first fee settles (#3006). */
function CreditPlanSettlementWatch({ orgId }: { orgId: string }) {
  useUpgradeSettlementPoll(orgId)
  return null
}

const CARD_WATCH_MS = 5 * 60_000
const CARD_WATCH_POLL_MS = 15_000

export const Route = createFileRoute('/_authenticated/orgs/$orgId/billing')({
  component: Billing,
  // Stripe-hosted Checkout sends the admin back here with ?checkout=success or
  // ?checkout=cancel (the return URLs are configured Lago-side). Absent on a
  // normal visit; validated so an unexpected value is dropped rather than
  // throwing.
  validateSearch: z.object({
    checkout: z.enum(['success', 'cancel']).optional(),
  }),
  loader: async ({ context }) => {
    return { crumb: 'Billing', metadata: context.metadata }
  },
})

function Billing() {
  const { organisationClient } = useClient()
  const { orgId } = Route.useParams()
  const { metadata } = Route.useLoaderData()
  const { org } = useLoaderData({ from: '/_authenticated/orgs/$orgId' })
  const search = Route.useSearch()
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()

  /* -------------------------- Payment-action return ------------------------ */

  // Declared ahead of the queries because the billing query polls while a
  // payment is awaiting confirmation; the effects that resolve these live in
  // the "Stripe / payment return" section below.
  //
  // Read-and-clear on mount: a snapshot is present exactly when this load is
  // the return from the hosted payment page (see lib/payment-return.ts).
  const [paymentReturn] = useState(() => takePaymentReturn(orgId))
  const [paymentConfirmed, setPaymentConfirmed] = useState(false)
  const [paymentTimedOut, setPaymentTimedOut] = useState(false)
  const [paymentReturnDismissed, setPaymentReturnDismissed] = useState(false)

  const paymentReturnStatus: PaymentReturnStatus | null =
    !paymentReturn || paymentReturnDismissed
      ? null
      : paymentConfirmed
        ? 'confirmed'
        : paymentTimedOut
          ? 'unconfirmed'
          : 'pending'

  /* ------------------------------- Queries ------------------------------- */

  const billingQuery = useQuery({
    queryKey: ['org-billing', orgId],
    queryFn: () => organisationClient.getOrgBillingState({ orgId }, metadata),
    // The payment lands asynchronously (Lago webhook), so the one fetch that
    // triggers it is always too early to see it. A return from the hosted
    // payment page (#2359) polls until its outcome is observed or its window
    // closes; outside that the page never polls on its own.
    refetchInterval: () =>
      paymentReturnStatus === 'pending' ? PAYMENT_CONFIRM_POLL_MS : false,
  })
  const billing = billingQuery.data

  const storageQuery = useQuery({
    queryKey: ['org-storage-status', orgId],
    queryFn: () => organisationClient.getOrgStorageStatus({ orgId }, metadata),
  })

  const invoicesQuery = useQuery({
    queryKey: ['org-invoices', orgId],
    queryFn: () =>
      organisationClient.listOrgBillingInvoices({ orgId }, metadata),
  })

  const billingMode = billing?.billingMode ?? org.billingMode

  // Starter is the free entry tier: it never incurs a charge, so it has no
  // invoices by definition. Suppress all invoice display for it — the local
  // Lago stub returns canned invoices for every org regardless of tier, and a
  // real starter org likewise has nothing to bill.
  const planTier = billing?.planTier ?? org.planTier
  // A credit plan keeps `plan_tier = 'starter'` until #3005, so Starter's
  // no-invoices, no-card, read-only-budget treatment applies only to its Trial.
  const creditPlanTier = org.creditPlanTier
  const creditPlan = creditPlanTier
    ? CREDIT_PLAN_CATALOGUE[creditPlanTier]
    : null
  const isStarter =
    planTier === 'starter' && (!creditPlanTier || creditPlanTier === 'trial')
  // Starter is a time-boxed trial (#2045); show how far through the 30-day
  // window the org is. `null` for non-Starter orgs and Starter orgs with no
  // stamped trial clock (e.g. test orgs), which hides the bar entirely.
  const trialProgress = isStarter
    ? starterTrialProgress(org.trialStartedAt, org.trialExpiresAt, new Date())
    : null
  const creditUsageQuery = useQuery({
    // The tier is in the key so a conversion landing re-reads the allowance.
    queryKey: ['org-credit-usage', orgId, creditPlanTier],
    queryFn: () => organisationClient.getOrgCreditUsage({ orgId }, metadata),
    enabled: creditPlan !== null,
  })

  /* ------------------------------ Mutations ------------------------------ */

  // A plan change moves the row, the usage and possibly the freeze, so all three
  // are re-read: the org loader is what lets go of a frozen layout.
  const refreshAfterPlanChange = () => {
    queryClient.invalidateQueries({ queryKey: ['org-billing', orgId] })
    queryClient.invalidateQueries({ queryKey: ['org-credit-usage', orgId] })
    router.invalidate({
      filter: (match) => match.routeId === '/_authenticated/orgs/$orgId',
    })
  }
  const changePlanMutation = useMutation({
    mutationFn: ({
      tier,
      afterCheckout,
    }: {
      tier: SelfServeCreditPlanTier
      afterCheckout: boolean
    }) =>
      organisationClient.changeOrgCreditPlan(
        { orgId, creditPlanTier: tier, afterCheckout },
        metadata,
      ),
    // A cancel from an earlier attempt says nothing about this one's outcome.
    onMutate: () => cancelPendingMutation.reset(),
    onSuccess: refreshAfterPlanChange,
    // A failure can still have moved the plan, in which case the server keeps the
    // claim. Only a re-read shows the admin that, and the way to cancel it.
    onError: refreshAfterPlanChange,
  })
  const cancelPendingMutation = useMutation({
    mutationFn: () =>
      organisationClient.cancelPendingOrgCreditPlan({ orgId }, metadata),
    onSuccess: refreshAfterPlanChange,
  })
  const cancelScheduledMutation = useMutation({
    mutationFn: () =>
      organisationClient.cancelScheduledOrgCreditPlanChange(
        { orgId },
        metadata,
      ),
    onSuccess: refreshAfterPlanChange,
  })
  // The server's refusal verbatim when it sent one, as on the plan card.
  const planChangeFailure = changePlanMutation.isError
    ? (billingActionFailure(changePlanMutation.error)?.message ??
      'Couldn’t change your plan. Please try again in a moment.')
    : null
  // An org created after its creator's trial was used has no plan yet (#3196).
  // It chooses one from the picker. A failed billing read shows the plan card
  // instead, for its error and retry: without the read the picker can't tell
  // whether there's a card, and would send the admin to Checkout regardless.
  const awaitingFirstPlan = isAwaitingFirstPlan(org)
  const choosesFirstPlan = awaitingFirstPlan && !billingQuery.isError
  const showsPlanCard = !awaitingFirstPlan || billingQuery.isError
  // Ops-managed plans aren't the admin's to change.
  const canChangeCreditPlan =
    choosesFirstPlan ||
    creditPlanTier === 'trial' ||
    creditPlanTier === 'pro' ||
    creditPlanTier === 'max' ||
    creditPlanTier === 'ultra'

  // Lago's stored card changes when Stripe's webhook lands, shortly after a
  // return from Checkout, so watch for the digits to change for a few minutes.
  const [cardWatchUntil] = useState(() =>
    search.checkout === 'success' ? Date.now() + CARD_WATCH_MS : 0,
  )
  const cardQuery = useQuery({
    queryKey: ['org-billing-card', orgId],
    queryFn: () => organisationClient.getOrgBillingCard({ orgId }, metadata),
    enabled: canChangeCreditPlan && (billing?.hasPaymentMethod ?? false),
    refetchInterval: () =>
      Date.now() < cardWatchUntil ? CARD_WATCH_POLL_MS : false,
  })

  // The fee settling means the page has to notice when it lands: nothing else
  // re-reads the org on its own. A claim that clears with the org still on its
  // Trial is one that failed.
  const pendingCreditPlanTier = org.pendingCreditPlanTier ?? null
  const [wasPending, setWasPending] = useState(false)
  // Kept across a reload and a cancelled Checkout, so the warning about retrying
  // the card still shows. A completed Checkout means they've changed it.
  const [conversionFailed, setConversionFailed] = useState(
    () => search.checkout !== 'success' && readCreditPlanDecline(orgId),
  )
  useEffect(() => {
    if (conversionFailed) rememberCreditPlanDecline(orgId)
    else forgetCreditPlanDecline(orgId)
  }, [conversionFailed, orgId])
  // A stored decline can outlast the Trial: the conversion may have completed while
  // this page wasn't mounted, or in another tab. Only a Trial, or an org choosing
  // its first plan, can have one.
  const convertsOnPayment = creditPlanTier === 'trial' || awaitingFirstPlan
  if (conversionFailed && !convertsOnPayment) {
    setConversionFailed(false)
  }
  if (pendingCreditPlanTier && !wasPending) {
    setWasPending(true)
    setConversionFailed(false)
  }
  if (!pendingCreditPlanTier && wasPending) {
    setWasPending(false)
    setConversionFailed(
      convertsOnPayment && cancelPendingMutation.data?.status !== 'cancelled',
    )
  }

  // The plan chosen before leaving for Checkout to add a card. Latched on arrival,
  // since the checkout marker is about to be stripped.
  const [creditPlanIntent] = useState(() =>
    search.checkout === 'success' ? readCreditPlanIntent(orgId) : null,
  )
  const creditPlanIntentStarted = useRef(false)
  useEffect(() => {
    if (search.checkout === 'cancel') forgetCreditPlanIntent(orgId)
  }, [search.checkout, orgId])
  useEffect(() => {
    if (!creditPlanIntent || creditPlanIntentStarted.current) return
    if (!canChangeCreditPlan) return
    // Waits for a settled billing read. The server re-checks the card, with its
    // own retry for one still landing.
    if (billingQuery.isPending || billingQuery.isFetching) return
    creditPlanIntentStarted.current = true
    forgetCreditPlanIntent(orgId)
    changePlanMutation.mutate({ tier: creditPlanIntent, afterCheckout: true })
    // `mutate` is stable; the mutation object isn't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creditPlanIntent,
    canChangeCreditPlan,
    billingQuery.isPending,
    billingQuery.isFetching,
    orgId,
  ])

  // The outbound Checkout leg — mint the URL, stash what the return needs,
  // leave for Stripe.
  const checkout = useBillingCheckout({
    orgId,
    metadata,
    hasPaymentMethod: billing?.hasPaymentMethod ?? false,
  })
  useEffect(() => {
    // No redirect happened, so nothing is coming back to act on it.
    if (checkout.isError) forgetCreditPlanIntent(orgId)
  }, [checkout.isError, orgId])

  const ceilingMutation = useMutation({
    mutationFn: (storageCeilingBytes: number | null) =>
      organisationClient.updateOrganisation(
        { orgId, storageCeilingBytes },
        metadata,
      ),
    onSuccess: () => {
      // The ceiling tightens the effective cap and lives on the org row.
      queryClient.invalidateQueries({ queryKey: ['org-storage-status', orgId] })
      router.invalidate()
    },
  })

  const budgetMutation = useMutation({
    mutationFn: (input: {
      monthlyBudgetCents: number | null
      budgetEnforce: boolean
    }) => organisationClient.updateOrganisation({ orgId, ...input }, metadata),
    onSuccess: () => {
      // Budget + enforce live on the org row that the parent loader resolves;
      // re-running it refreshes the spend-vs-budget card.
      router.invalidate()
    },
  })

  /* ------------------------- Payment-action redirect ----------------------- */

  // Leave for the Stripe-hosted page that settles the outstanding invoice —
  // the "Complete payment" CTA offered by the credit plan picker (a
  // conversion's fee) and the payment-method card (3-D Secure step-up).
  //
  // Unlike Checkout there is no way to mark the return URL (it's Lago
  // provider-level config), so we leave the marker on this side instead:
  // snapshot the figures a successful payment has to move, and recognise the
  // change on the way back (#2359).
  const openPaymentAction = () => {
    if (!billing?.paymentActionUrl) return
    rememberPaymentReturn(orgId, {
      planTier: billing.planTier,
      invoicePaymentStatus: billing.lastInvoice?.paymentStatus ?? null,
      startedAt: Date.now(),
    })
    // Lago mints this page from the same Stripe provider whose
    // `success_redirect_url` points at the org-agnostic
    // `/billing/checkout-return` route. If it redirects there, that route needs
    // a stashed org or it bounces the admin to the org list; stashing one costs
    // nothing on the paths that return some other way.
    rememberCheckoutReturnOrg(orgId)
    window.location.assign(billing.paymentActionUrl)
  }

  /* --------------------------- Stripe / payment return --------------------- */

  // Captured at mount rather than in the effect below. A Checkout return is a
  // fresh document load carrying the marker, so mount is exactly when it is
  // known — the same read-and-clear shape `paymentReturn` uses above. Setting it
  // from an effect committed one render with the marker in the URL but no
  // banner, and put a setState in the effect body (a cascading render).
  //
  // A payment-action return can arrive carrying this same marker (both
  // redirects share Lago's provider-level `success_redirect_url`). The payment
  // banner is the accurate one in that case — "payment method submitted" is not
  // what happened — so let it own the slot and only strip the param below.
  const [returnStatus, setReturnStatus] = useState<'success' | 'cancel' | null>(
    () => (search.checkout && !paymentReturn ? search.checkout : null),
  )

  // Whether this org had a card before the Checkout we are returning from
  // (#2668) — `null` when unknown. Read-and-clear at mount, the same shape
  // `paymentReturn` uses, and for the same reason: the
  // stash belongs to the redirect this load IS, and leaving it behind would let
  // it decide some later, unrelated checkout.
  const [hadPaymentMethodBefore] = useState(() =>
    search.checkout ? takeCheckoutHadPaymentMethod() : null,
  )

  // Retire the success banner once the billing state confirms the card, rather
  // than leaving it to a hand dismissal it may never get (#2668). Only on the
  // add-card path — see `isCheckoutReturnConfirmed` for why a replacement
  // cannot be confirmed from here at all. Decided during render: it is a pure
  // question about state already held, and the `paymentConfirmed` latch above
  // sets the precedent for answering it here instead of in an effect that would
  // cost a cascading render.
  if (
    returnStatus === 'success' &&
    isCheckoutReturnConfirmed(billing, hadPaymentMethodBefore)
  ) {
    setReturnStatus(null)
  }

  // On return from Stripe Checkout: refetch the (now-changed) billing state +
  // parent org, then strip the query param so a refresh doesn't re-trigger this.
  useEffect(() => {
    if (!search.checkout) return
    queryClient.invalidateQueries({ queryKey: ['org-billing', orgId] })
    router.invalidate()
    navigate({
      to: '/orgs/$orgId/billing',
      params: { orgId },
      search: {},
      replace: true,
    })
  }, [search.checkout, orgId, queryClient, navigate, router])

  // Watch the refetched billing state for the payment landing, and latch the
  // first sighting — "confirmed" must be a one-way door, or the banner would
  // flicker with any later read that disagrees (#2360). On confirmation, re-run the parent org loader so
  // the cards that read the org row catch up with the banner.
  //
  // Latching is a pure decision about state we already hold, so it happens
  // during render; the effect below only performs the side effect it implies.
  if (
    paymentReturn &&
    !paymentConfirmed &&
    isPaymentConfirmed(paymentReturn, billing)
  ) {
    setPaymentConfirmed(true)
  }

  // Latched on a ref, not just on `paymentConfirmed`: that flag now stays true
  // for the rest of the page's life, so without this the loader would re-run on
  // any later change to `router`'s identity. The old effect got this for free
  // because `paymentConfirmed` was both the trigger and the early-out.
  const parentReloadedRef = useRef(false)
  useEffect(() => {
    if (!paymentConfirmed || parentReloadedRef.current) return
    parentReloadedRef.current = true
    router.invalidate()
  }, [paymentConfirmed, router])

  // Close the watch window on a wall clock rather than on refetches: a billing
  // query that is erroring never delivers one, and the banner must not sit on
  // "Confirming…" forever. Timed from the return, not from the redirect — the
  // admin may have spent minutes on the 3-D Secure challenge.
  useEffect(() => {
    if (!paymentReturn) return
    const timer = window.setTimeout(
      () => setPaymentTimedOut(true),
      PAYMENT_CONFIRM_TIMEOUT_MS,
    )
    return () => window.clearTimeout(timer)
  }, [paymentReturn])

  return (
    <div className="space-y-5">
      {paymentReturnStatus ? (
        <PaymentReturnBanner
          status={paymentReturnStatus}
          onRefresh={() => billingQuery.refetch()}
          onDismiss={() => setPaymentReturnDismissed(true)}
        />
      ) : (
        returnStatus && (
          <CheckoutReturnBanner
            status={returnStatus}
            onDismiss={() => setReturnStatus(null)}
          />
        )
      )}

      {/* Plan */}
      {showsPlanCard && (
        <BillingPlanCard
          planTier={planTier}
          creditPlan={creditPlan && { displayName: creditPlan.displayName }}
          billingMode={billingMode}
          entitlements={
            billing?.entitlements ??
            BILLING_PLAN_CATALOGUE[org.planTier].entitlements
          }
          trialProgress={trialProgress}
          isLoading={billingQuery.isLoading}
          isError={billingQuery.isError}
          onRetry={() => billingQuery.refetch()}
        />
      )}

      {pendingCreditPlanTier && <CreditPlanSettlementWatch orgId={orgId} />}
      {canChangeCreditPlan && (creditPlanTier || choosesFirstPlan) && (
        <BillingCreditPlanPicker
          currentTier={creditPlanTier ?? null}
          pendingTier={pendingCreditPlanTier}
          paymentActionUrl={billing?.paymentActionUrl ?? null}
          isCancellingPending={cancelPendingMutation.isPending}
          onCancelPending={() => cancelPendingMutation.mutate()}
          cancelPendingMessage={
            cancelPendingMutation.data?.status === 'payment_succeeded'
              ? 'Your payment has already gone through, so this can’t be cancelled. Your new plan is starting now.'
              : cancelPendingMutation.isError
                ? (billingActionFailure(cancelPendingMutation.error)?.message ??
                  'Couldn’t cancel that change. Please try again in a moment.')
                : null
          }
          conversionFailed={conversionFailed}
          onChangeCard={() => {
            forgetCreditPlanIntent(orgId)
            checkout.startCheckout()
          }}
          scheduledChange={
            creditUsageQuery.data?.usage?.scheduledChange ?? null
          }
          periodEndsAt={creditUsageQuery.data?.usage?.resetsAt ?? null}
          hasPaymentMethod={billing?.hasPaymentMethod ?? false}
          card={cardQuery.data?.card ?? null}
          isChanging={changePlanMutation.isPending}
          isOpeningCheckout={checkout.isPending}
          changeError={
            planChangeFailure ??
            (checkout.isError
              ? (billingActionFailure(checkout.error)?.message ??
                'Couldn’t open the card form. Please try again in a moment.')
              : null)
          }
          onChange={(tier) => {
            checkout.reset()
            if (billing?.hasPaymentMethod) {
              changePlanMutation.mutate({ tier, afterCheckout: false })
              return
            }
            rememberCreditPlanIntent(orgId, tier)
            checkout.startCheckout()
          }}
          isCancellingScheduled={cancelScheduledMutation.isPending}
          cancelError={cancelScheduledMutation.isError}
          onCancelScheduled={() => cancelScheduledMutation.mutate()}
        />
      )}

      {creditPlan && (
        <BillingCreditsCard
          usage={creditUsageQuery.data?.usage ?? null}
          isLoading={creditUsageQuery.isLoading}
          isError={creditUsageQuery.isError}
          onRetry={() => creditUsageQuery.refetch()}
        />
      )}

      {/* Payment Method — hidden on legacy Starter: it's a free trial with no
          charges, so there's nothing to collect a card for. A credit Trial sees
          it once it has a card, per `showsPaymentMethodCard`. */}
      {showsPaymentMethodCard({
        isStarter,
        creditPlanTier,
        hasPaymentMethod: billing?.hasPaymentMethod ?? false,
      }) && (
        <BillingPaymentMethodCard
          hasPaymentMethod={billing?.hasPaymentMethod ?? false}
          // Only prompt for a card on a plan that actually gets card-charged.
          // Enterprise and the unlisted tiers are funded without one, so their
          // steady state would otherwise read as a setup gap (#2359).
          // An overage plan with no card is held to its bundle (#3002), whatever
          // the row's ADR-0013 tier still says. A per-org bundle is exempt from
          // that hold, so its steady state without a card isn't a setup gap.
          requiresPaymentMethod={
            creditPlanTier && creditPlan
              ? // A Trial only reaches this card with a card to manage.
                creditPlanTier === 'trial' ||
                (creditPlan.atBundleLimit === 'overage' &&
                  !hasPerOrgBundle(creditPlanTier))
              : requiresSelfServePaymentMethod(planTier)
          }
          paymentState={billing?.paymentState ?? 'current'}
          // A Trial owes nothing, and its only invoices are $0 closers.
          lastInvoice={
            creditPlanTier === 'trial' ? null : (billing?.lastInvoice ?? null)
          }
          paymentActionUrl={billing?.paymentActionUrl ?? null}
          isLoading={billingQuery.isLoading}
          isError={billingQuery.isError}
          isActionPending={checkout.isPending}
          actionError={checkout.isError}
          onAction={() => {
            forgetCreditPlanIntent(orgId)
            checkout.startCheckout()
          }}
          onCompletePayment={openPaymentAction}
          onRetry={() => billingQuery.refetch()}
        />
      )}

      {/* Budget — Starter is a trial where a customer budget adds no value:
          force it off and make the card read-only, but keep it visible so the
          feature stays known. */}
      {/* On a credit plan this is the overage cap (ADR-0027 D5). A plan that stops
          at its bundle has no overage to cap, and Custom's gate never stops, so
          its enforce toggle would promise something that doesn't happen. */}
      {((creditPlan === null && !awaitingFirstPlan) ||
        creditPlan?.atBundleLimit === 'overage') && (
        <BillingBudgetCard
          monthlyBudgetCents={isStarter ? null : org.monthlyBudgetCents}
          budgetEnforce={org.budgetEnforce}
          budgetEditable={!isStarter}
          monthlySpendCurrent={org.monthlySpendCurrent}
          overage={
            creditPlan
              ? {
                  creditsWarningAt: org.creditsWarningAt ?? null,
                  creditsReachedAt: org.creditsReachedAt ?? null,
                  emailsCreditAlerts:
                    !!creditPlanTier && !hasPerOrgBundle(creditPlanTier),
                }
              : undefined
          }
          monthlySpendLimit={org.monthlySpendLimit}
          budgetWarningAt={org.budgetWarningAt}
          budgetReachedAt={org.budgetReachedAt}
          isActionPending={budgetMutation.isPending}
          actionError={budgetMutation.isError}
          onSave={(input) => budgetMutation.mutate(input)}
        />
      )}

      {/* Storage */}
      <BillingStorageCard
        status={storageQuery.data ?? null}
        storageSummary={org.storageSummary}
        isLoading={storageQuery.isLoading}
        isError={storageQuery.isError}
        isActionPending={ceilingMutation.isPending}
        actionError={ceilingMutation.isError}
        onSaveCeiling={(bytes) => ceilingMutation.mutate(bytes)}
        onRetry={() => storageQuery.refetch()}
      />

      {/* Invoices — starter has none by definition (see isStarter above). */}
      <BillingInvoicesCard
        invoices={isStarter ? [] : (invoicesQuery.data ?? [])}
        isLoading={invoicesQuery.isLoading}
        isError={invoicesQuery.isError}
        onRetry={() => invoicesQuery.refetch()}
      />
    </div>
  )
}
